import { z } from 'zod'

import {
  accept,
  isPlayersTurn,
  reject,
  type ActionContext,
  type GameEngine,
  type PlaySummary,
  type Player,
  type TurnState,
} from '../engine.js'
import type { Rng } from '../rng.js'
import { FLUENT_MS } from './maths-adaptive.js'
import { levelOf, MAX_LEVEL, MIN_LEVEL, STARTING_LEVEL, type SkillLevels } from './maths-skills.js'
import { TROLLEY_SKILLS, type TrolleyQuestion, type TrolleySkill } from './trolley-questions.js'
import { TRIES_PER_QUESTION } from './maths.js'
import type { Item } from './trolley-shop.js'

/**
 * Turtley's Trolley.
 *
 * The maths she will meet in a word problem rather than as a sum. She can
 * already do the arithmetic in Monkey's Maths; the work here is deciding
 * *which* arithmetic from a sentence, which is a different skill and the one
 * that makes maths feel hard at school.
 *
 * Its skill levels are its own, deliberately. Being fluent at times tables
 * says little about whether a sentence makes sense to her, and folding them
 * together would move her maths level for the wrong reason.
 */
export const DEFAULT_TROLLEY_ROUND = 6

export const trolleyAction = z.object({
  type: z.literal('answer'),
  /** In cents, or a plain count. Whole numbers only, like the number pad. */
  value: z.number().int(),
})

export type TrolleyAction = z.infer<typeof trolleyAction>

export interface TrolleyConfig {
  readonly levels?: SkillLevels
  readonly roundLength?: number
  readonly startedAt?: number
}

export interface TrolleyAttempt {
  readonly skillKey: string
  readonly prompt: string
  readonly expected: number
  readonly given: number
  readonly correct: boolean
  readonly elapsedMs: number
  readonly playerId: string
}

export interface TrolleyState {
  readonly queue: readonly TrolleyQuestion[]
  readonly levels: SkillLevels
  readonly roundLength: number
  readonly attempts: readonly TrolleyAttempt[]
  readonly lastAnswer: TrolleyAttempt | null
  readonly lastAnswerHadMoreGoes: boolean
  readonly correctCount: number
  readonly status: 'playing' | 'finished'
  readonly turn: TurnState
  readonly startedAt: number
  readonly askedAt: number
  readonly triesOnCurrent: number
}

export interface TrolleyView {
  /** The shop, so she can see what things cost while she works. */
  readonly prices: readonly Item[]
  readonly prompt: string | null
  readonly answerKind: 'money' | 'count' | null
  readonly correct: number
  readonly roundLength: number
  readonly status: TrolleyState['status']
  readonly currentPlayerId: string | null
  readonly lastAnswer:
    | (Pick<TrolleyAttempt, 'prompt' | 'expected' | 'given' | 'correct'> & {
        readonly moreGoes: boolean
      })
    | null
  readonly triesLeft: number
  readonly wrongAnswers: number
}

/** Which kinds of problem she has reached, by the same rule Monkey uses. */
export function availableTrolleySkills(levels: SkillLevels): readonly TrolleySkill[] {
  const reached = overallTrolleyLevel(levels)
  return TROLLEY_SKILLS.filter((skill) => skill.unlocksAt <= reached)
}

export function overallTrolleyLevel(levels: SkillLevels): number {
  const known = TROLLEY_SKILLS.map((skill) => levels[skill.key]).filter(
    (level): level is number => level !== undefined,
  )

  if (known.length === 0) return STARTING_LEVEL

  return known.reduce((total, level) => total + level, 0) / known.length
}

/** Weighted toward the kinds she finds hard, with a floor so a round is a mix. */
function chooseTrolleySkill(levels: SkillLevels, rng: Rng): TrolleySkill {
  const available = availableTrolleySkills(levels)
  // There is always at least one: the easiest unlocks at zero and an overall
  // level cannot fall below it.
  const pool = available.length > 0 ? available : [TROLLEY_SKILLS[0]!]

  const weights = pool.map((skill) => 1 + (MAX_LEVEL - levelOf(levels, skill.key)))
  const total = weights.reduce((sum, weight) => sum + weight, 0)

  let ticket = rng.int(total)
  for (const [index, weight] of weights.entries()) {
    ticket -= weight
    if (ticket < 0) return pool[index]!
  }

  return pool[pool.length - 1]!
}

/** Moves a level after an answer, the same way Monkey's does. */
function applyTrolleyAnswer(
  levels: SkillLevels,
  skillKey: string,
  correct: boolean,
  elapsedMs: number,
): SkillLevels {
  const level = levelOf(levels, skillKey)

  // Right and quick moves her on; right but slow holds; wrong steps back. A
  // word problem worked out slowly is still understood, which is why slow and
  // correct is not treated as a failure.
  const next = correct
    ? elapsedMs <= FLUENT_MS * 2
      ? Math.min(MAX_LEVEL, level + 1)
      : level
    : Math.max(MIN_LEVEL, level - 1)

  return { ...levels, [skillKey]: next }
}

/** Only the skills this round practised, for writing levels back. */
export function trolleyLevelsTouched(state: TrolleyState): SkillLevels {
  const touched = new Set(state.attempts.map((attempt) => attempt.skillKey))

  return Object.fromEntries(
    [...touched].map((skillKey) => [skillKey, levelOf(state.levels, skillKey)]),
  )
}

function buildQueue(levels: SkillLevels, roundLength: number, rng: Rng): TrolleyQuestion[] {
  return Array.from({ length: roundLength }, () => chooseTrolleySkill(levels, rng).build(rng))
}

function requeue(
  queue: readonly TrolleyQuestion[],
  question: TrolleyQuestion,
): readonly TrolleyQuestion[] {
  const rest = queue.slice(1)
  const at = Math.min(2, rest.length)
  return [...rest.slice(0, at), question, ...rest.slice(at)]
}

export const trolleyEngine: GameEngine<TrolleyConfig, TrolleyState, TrolleyAction> = {
  key: 'trolley',
  actionSchema: trolleyAction,

  createInitialState(config: TrolleyConfig, players: readonly Player[], rng: Rng): TrolleyState {
    const levels = config.levels ?? {}
    const roundLength = config.roundLength ?? DEFAULT_TROLLEY_ROUND

    return {
      queue: buildQueue(levels, roundLength, rng),
      levels,
      roundLength,
      attempts: [],
      lastAnswer: null,
      lastAnswerHadMoreGoes: false,
      correctCount: 0,
      status: 'playing',
      turn: { players, currentPlayerId: players[0]?.id ?? null },
      startedAt: config.startedAt ?? 0,
      askedAt: config.startedAt ?? 0,
      triesOnCurrent: 0,
    }
  },

  reduce(state, action, context: ActionContext) {
    if (state.status !== 'playing') return reject(state, 'game-over')
    if (!isPlayersTurn(state.turn, context.playerId)) return reject(state, 'not-your-turn')

    const current = state.queue[0]
    if (!current) return reject(state, 'game-over')

    const elapsedMs = Math.max(0, context.now - state.askedAt)
    const correct = action.value === current.answer

    const attempt: TrolleyAttempt = {
      skillKey: current.skillKey,
      prompt: current.prompt,
      expected: current.answer,
      given: action.value,
      correct,
      elapsedMs,
      playerId: context.playerId,
    }

    const attempts = [...state.attempts, attempt]
    const tries = correct ? 0 : state.triesOnCurrent + 1
    // The same three goes as Monkey's, for the same reason: being told to try
    // again is how somebody finds an answer themselves.
    const tryAgain = !correct && tries < TRIES_PER_QUESTION

    const queue = correct
      ? state.queue.slice(1)
      : tryAgain
        ? state.queue
        : requeue(state.queue, current)

    const finished =
      queue.length === 0 || attempts.length >= state.roundLength * (TRIES_PER_QUESTION + 1)

    return accept({
      ...state,
      queue,
      levels: applyTrolleyAnswer(state.levels, current.skillKey, correct, elapsedMs),
      attempts,
      lastAnswer: attempt,
      lastAnswerHadMoreGoes: tryAgain,
      correctCount: correct ? state.correctCount + 1 : state.correctCount,
      status: finished ? 'finished' : 'playing',
      askedAt: context.now,
      triesOnCurrent: tryAgain ? tries : 0,
      turn: { ...state.turn, currentPlayerId: state.turn.currentPlayerId },
    })
  },

  isTerminal(state) {
    return state.status === 'finished'
  },

  summarise(state, endedAt): PlaySummary {
    return {
      gameKey: 'trolley',
      result: state.correctCount === state.roundLength ? 'won' : 'abandoned',
      durationMs: Math.max(0, endedAt - state.startedAt),
      difficulty: `level ${Math.round(overallTrolleyLevel(state.levels))}`,
    }
  },
}

export function toTrolleyView(state: TrolleyState): TrolleyView {
  const current = state.queue[0]

  return {
    prices: current?.prices ?? [],
    prompt: current?.prompt ?? null,
    answerKind: current?.answerKind ?? null,
    correct: state.correctCount,
    roundLength: state.roundLength,
    status: state.status,
    currentPlayerId: state.turn.currentPlayerId,
    lastAnswer: state.lastAnswer
      ? {
          prompt: state.lastAnswer.prompt,
          expected: state.lastAnswer.expected,
          given: state.lastAnswer.given,
          correct: state.lastAnswer.correct,
          moreGoes: state.lastAnswerHadMoreGoes,
        }
      : null,
    triesLeft: Math.max(0, TRIES_PER_QUESTION - state.triesOnCurrent),
    wrongAnswers: state.attempts.filter((attempt) => !attempt.correct).length,
  }
}
